import { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Building2, Upload, X } from "lucide-react";
import { getDownloadURL, ref, uploadBytesResumable } from "firebase/storage";
import { storage } from "@/config/firebaseConfig";
import { useToast } from "@/hooks/use-toast";

interface LogoPromptDialogProps {
  isOpen: boolean;
  onComplete: () => void;
  businessUid: string;
}

export const LogoPromptDialog = ({
  isOpen,
  onComplete,
  businessUid,
}: LogoPromptDialogProps) => {
  const [isUploading, setIsUploading] = useState(false);
  const [logoUrl, setLogoUrl] = useState<string>("");
  const [uploadProgress, setUploadProgress] = useState(0);
  const { toast } = useToast();

  useEffect(() => {
    // Check if logo already exists
    if (isOpen && businessUid) {
      getDownloadURL(ref(storage, `businessUsersAvatars/${businessUid}/profile_image`))
        .then((url) => {
          setLogoUrl(url);
        })
        .catch(() => {
          // No logo exists, which is expected for this prompt
          setLogoUrl("");
        });
    }
  }, [isOpen, businessUid]);

  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    const isJpgOrPng = file.type === "image/jpeg" || file.type === "image/png";
    if (!isJpgOrPng) {
      toast({
        variant: "destructive",
        title: "Felaktigt filformat",
        description: "Endast JPG eller PNG-filer är tillåtna.",
      });
      return;
    }

    // Validate file size (2MB)
    const isLt2M = file.size / 1024 / 1024 < 2;
    if (!isLt2M) {
      toast({
        variant: "destructive",
        title: "Filen är för stor",
        description: "Bilden måste vara mindre än 2MB.",
      });
      return;
    }

    setIsUploading(true);
    setUploadProgress(0);

    const storageRef = ref(
      storage,
      `businessUsersAvatars/${businessUid}/profile_image`
    );
    const uploadTask = uploadBytesResumable(storageRef, file);

    uploadTask.on(
      "state_changed",
      (snapshot) => {
        const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
        setUploadProgress(Math.round(progress));
      },
      (error) => {
        console.error("Upload error:", error);
        toast({
          variant: "destructive",
          title: "Uppladdningen misslyckades",
          description: "Försök igen eller kontakta support om problemet kvarstår.",
        });
        setIsUploading(false);
      },
      async () => {
        const downloadURL = await getDownloadURL(storageRef);
        setLogoUrl(downloadURL);
        setIsUploading(false);
        toast({
          title: "Logotyp uppladdad!",
          description: "Din logotyp har sparats och kommer visas i annonsen.",
        });
      }
    );
  };

  const handleContinue = () => {
    if (logoUrl) {
      onComplete();
    } else {
      // User chose to skip - show confirmation
      toast({
        title: "Du kan lägga till logotyp senare",
        description: "Gå till Mina sidor > Företagsinformation för att ladda upp din logotyp.",
      });
      onComplete();
    }
  };

  return (
    <Dialog
      open={isOpen}
      onOpenChange={
        // Dialog is controlled by parent; ignore close requests from overlay/Escape
        () => {
          // Do nothing
        }
      }
    >
      <DialogContent className="sm:max-w-md" onPointerDownOutside={(e) => e.preventDefault()}>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Building2 className="h-5 w-5 text-primary" />
            Lägg till företagets logotyp innan du skapar annonsen
          </DialogTitle>
          <DialogDescription className="pt-2">
            En logotyp gör din annons mer professionell och igenkännbar. Kandidater ser den när de matchar med din tjänst.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Logo Preview/Upload Area */}
          <div className="flex flex-col items-center">
            {logoUrl ? (
              <div className="relative">
                <img
                  src={logoUrl}
                  alt="Företagslogotyp"
                  className="w-32 h-32 rounded-full object-cover border-4 border-primary/20"
                />
                <button
                  onClick={() => setLogoUrl("")}
                  className="absolute top-0 right-0 bg-destructive text-white rounded-full p-1 hover:bg-destructive/90 transition-colors"
                  aria-label="Ta bort logotyp"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            ) : (
              <label
                htmlFor="logo-upload"
                className={`w-32 h-32 rounded-full border-2 border-dashed border-primary flex flex-col items-center justify-center cursor-pointer transition-colors hover:bg-primary/5 ${
                  isUploading ? "opacity-50 cursor-not-allowed" : ""
                }`}
              >
                {isUploading ? (
                  <div className="text-center">
                    <Upload className="h-8 w-8 text-primary mx-auto mb-2 animate-pulse" />
                    <p className="text-xs text-muted-foreground">{uploadProgress}%</p>
                  </div>
                ) : (
                  <>
                    <Upload className="h-8 w-8 text-primary mb-2" />
                    <span className="text-xs text-muted-foreground text-center px-2">
                      Klicka för att ladda upp
                    </span>
                  </>
                )}
                <input
                  id="logo-upload"
                  type="file"
                  accept="image/jpeg,image/png"
                  onChange={handleFileSelect}
                  disabled={isUploading}
                  className="hidden"
                />
              </label>
            )}
          </div>

          {/* Info Text */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 space-y-1">
            <p className="text-xs text-blue-900 font-medium">Tips för bästa resultat:</p>
            <ul className="text-xs text-blue-800 space-y-0.5 ml-4 list-disc">
              <li>Använd en fyrkantig bild (visar bäst i cirkel)</li>
              <li>Max 2MB, JPG eller PNG-format</li>
              <li>Tydlig och väldefinierad logotyp</li>
            </ul>
          </div>
        </div>

        <DialogFooter className="flex-col sm:flex-row gap-2">
          <Button
            variant="outline"
            onClick={handleContinue}
            disabled={isUploading}
            className="w-full sm:w-auto"
          >
            {logoUrl ? "Fortsätt" : "Hoppa över"}
          </Button>
          {logoUrl && (
            <Button
              onClick={handleContinue}
              disabled={isUploading}
              className="w-full sm:w-auto"
            >
              Fortsätt med logotyp
            </Button>
          )}
          {!logoUrl && !isUploading && (
            <Button
              onClick={() => document.getElementById("logo-upload")?.click()}
              className="w-full sm:w-auto"
            >
              <Upload className="h-4 w-4 mr-2" />
              Välj fil
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

